import React from 'react';
import { motion } from "framer-motion";

const experiences = [
  {
    year: "May 2024 - Present",
    role: "Senior Software Engineer",
    company: "Walmart | Sam's Club Supply Chain Technology",
    challenge: "High-latency legacy APIs were slowing retail operations.",
    solution: "Architected a type-safe JPA Criteria abstraction layer and re-engineered critical APIs. The Impact: Achieved 4–6× faster response times and an 80% reduction in cloud infrastructure costs.",
    skills: ["JDK 25", "Spring Boot 3.5+", "JPA Criteria API", "React", "GCP", "Azure", "GenAI", "XGBoost"]
  },
  {
    year: "Jan 2022 - April 2024",
    role: "Software Engineer III – Full Stack / Lead",
    company: "Walmart | Sam's Club Supply Chain Technology",
    challenge: "Manual bottlenecks in data science feature testing.",
    solution: "Developed Python-based pipelines on Databricks and built a specialized web testing tool. The Impact: Reduced feature testing effort from three days to under three hours while migrating legacy apps to WCNP.",
    skills: ["Java", "Spring Boot", "Angular", "Python", "Databricks", "Azure Data Factory", "WCNP"]
  },
  {
    year: "March 2020 - Dec 2021",
    role: "Application Architect - Full Stack Java",
    company: "Wipro Technologies | FedEx Services",
    challenge: "Modernizing legacy middleware for logistics messaging.",
    solution: "Developed cloud-native JMS microservices and Angular UI modules for real-time monitoring. The Impact: Delivered enterprise integration solutions with 0 UAT defects and received the 'Fantastic' Award.",
    skills: ["Spring Boot", "IBM MQ", "Angular", "PCF", "JMS", "TDD/XP"]
  },
  {
    year: "Jan 2019 - Feb 2020",
    role: "Senior Software Engineer",
    company: "Wipro Technologies | Client: Walmart Inc. (Digital Twin)",
    challenge: "Excessive manual overhead in processing Splunk logs for simulations.",
    solution: "Developed an end-to-end framework using React and Spring Boot to automate log ingestion. The Impact: Eliminated manual data preparation bottlenecks for the architecture team.",
    skills: ["Java", "Spring Boot", "React JS", "Splunk SDK", "Cassandra", "MongoDB"]
  },
  {
    year: "Oct 2018 - Dec 2018",
    role: "Senior Software Engineer",
    company: "Wipro Technologies | Client: Electrolux Inc.",
    challenge: "Restoring a damaged client relationship after previous project failures. ",
    solution: "Gained trust through transparency and proactively developed unlisted 'value-add' features. The Impact: Delivered a 0-defect release ahead of schedule and secured a long-term partnership.",
    skills: ["Java", "Spring Boot", "Angular JS", "REST", "Maven", "SQL"]
  },
  {
    year: "Sept 2015 - Sept 2018",
    role: "Senior Software Engineer",
    company: "Wipro Technologies | Client: Walmart Inc. (NextGen Pricing)",
    challenge: "Ensuring zero-defect stability for high-visibility pricing systems.",
    solution: "Individually engineered a custom automation tool covering 90% of regression cases. The Impact: Achieved 0-defect delivery in UAT, earning recognition from Director and President levels.",
    skills: ["Java", "Selenium", "SQL", "SAP Hana", "Cassandra", "Cucumber"]
  },
  {
    year: "Oct 2010 - Aug 2015",
    role: "Software Developer / QA Automation Engineer",
    company: "Wipro Technologies | Client: Olympus Inc.",
    description: "Started career in software testing and automation, developing Selenium-based test frameworks. Progressed to full-stack development, working on enterprise retail and manufacturing projects. Earned multiple awards for performance and quality delivery.",
    skills: ["Java", "Selenium", "Automation Testing", "SQL", "Web Development"]
  },
  {
    year: "May 2009 - Oct 2010",
    role: "System Administrator",
    company: "Retail Industry",
    description: "Managed IT systems and inventory operations for retail business. Handled stock management, sales reporting, and system administration. Developed foundational technical and business process skills.",
    skills: ["System Administration", "Business Operations", "Reporting", "IT Support"]
  }
];

export function Timeline() {
  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.4, 0.25, 1],
      }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.45,
        ease: [0.25, 0.4, 0.25, 1],
      }
    }
  };

  return (
    <section
      id="experience"
      className="section timeline-section"
    >
      <motion.h3
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.5 }}
        variants={fadeUp}
      >
        EXPERIENCE
      </motion.h3>

      <div className="timeline">
        {experiences.map((exp, index) => (
          <motion.div
            key={index}
            className={`timeline-item ${index === 0 ? 'timeline-item-current' : 'timeline-item-past'}`}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            variants={slideInLeft}
          >
            <div className={`timeline-marker ${index === 0 ? 'timeline-marker-current' : 'timeline-marker-past'}`}></div>
            <span className="timeline-year timeline-year-rail">{exp.year}</span>
            <div className="timeline-content">
              <span className="timeline-year timeline-year-mobile">{exp.year}</span>
              <h4 className="timeline-role">{exp.role}</h4>
              <span className="timeline-company">{exp.company}</span>
              {exp.challenge && exp.challenge.trim() !== "" && (
                <p className="timeline-description">
                  <span className="timeline-label challenge-label">Challenge:</span>
                  {exp.challenge}
                </p>
              )}

              {/* Render Solution if it exists and is not blank */}
              {exp.solution && exp.solution.trim() !== "" && (
                <p className="timeline-description">
                  <span className="timeline-label solution-label">Solution:</span>
                  {exp.solution}
                </p>
              )}
              <p className="timeline-description">{exp.description}</p>
              <div className="timeline-skills">
                {exp.skills.map((skill, idx) => (
                  <span key={idx} className="timeline-skill">{skill}</span>
                ))}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
